import type { ThemeConfig } from 'antd';
import type { ModuleKey } from './modules';
import { fontFamilySans } from './tipografia';

/**
 * Tokens de tema. A Direção Estética do escopo pede que cada módulo passe a
 * sensação de produto novo, sem deixar de ser a mesma plataforma: o esqueleto
 * (tipografia, raio, espaçamento) é comum e a cor de marca muda por módulo.
 *
 * As cores abaixo são ponto de partida, com as telas do QConcursos como
 * referência; serão substituídas quando a identidade visual for definida.
 */

/**
 * Tom claro de uma cor de marca, sobre fundo branco.
 *
 * O Antd deriva os tons de seleção da cor primária, mas a derivação supõe cores
 * vivas: um verde escuro e pouco saturado como o nosso vira `#96a3a1`, um
 * cinza-esverdeado sujo que aparecia no item selecionado do menu e na linha
 * selecionada das tabelas. Misturar com branco dá o tom claro que se espera.
 */
export function tomClaro(cor: string, proporcao: number): string {
  const numero = Number.parseInt(cor.slice(1), 16);
  const canais = [(numero >> 16) & 255, (numero >> 8) & 255, numero & 255];

  return `#${canais
    .map((canal) => Math.round(255 * (1 - proporcao) + canal * proporcao))
    .map((canal) => canal.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * A mesma cor, clara o bastante para o modo escuro.
 *
 * O algoritmo escuro do Antd foi feito para cores vivas: dado um verde já
 * escuro como o nosso, ele escurece ainda mais e o texto selecionado sai
 * `#1d615e` sobre fundo preto. Subimos a luminosidade preservando matiz e
 * saturação, que é o que mantém a cor reconhecível como a da marca.
 */
export function paraModoEscuro(cor: string, luminosidadeAlvo = 0.46): string {
  const numero = Number.parseInt(cor.slice(1), 16);
  const [r, g, b] = [((numero >> 16) & 255) / 255, ((numero >> 8) & 255) / 255, (numero & 255) / 255];

  const maior = Math.max(r, g, b);
  const menor = Math.min(r, g, b);
  const luminosidade = (maior + menor) / 2;

  if (luminosidade >= luminosidadeAlvo) return cor;

  const delta = maior - menor;
  const saturacao = delta === 0 ? 0 : delta / (1 - Math.abs(2 * luminosidade - 1));

  let matiz = 0;
  if (delta !== 0) {
    if (maior === r) matiz = ((g - b) / delta) % 6;
    else if (maior === g) matiz = (b - r) / delta + 2;
    else matiz = (r - g) / delta + 4;
    matiz *= 60;
    if (matiz < 0) matiz += 360;
  }

  const c = (1 - Math.abs(2 * luminosidadeAlvo - 1)) * saturacao;
  const x = c * (1 - Math.abs(((matiz / 60) % 2) - 1));
  const m = luminosidadeAlvo - c / 2;

  const faixas: [number, number, number][] = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ];
  const [rl, gl, bl] = faixas[Math.floor(matiz / 60) % 6]!;

  return `#${[rl, gl, bl]
    .map((canal) => Math.round((canal + m) * 255))
    .map((canal) => canal.toString(16).padStart(2, '0'))
    .join('')}`;
}

/** No escuro, o mesmo papel é feito por um véu translúcido da cor. */
export function veuDaCor(cor: string, opacidade: number): string {
  const numero = Number.parseInt(cor.slice(1), 16);
  return `rgba(${(numero >> 16) & 255}, ${(numero >> 8) & 255}, ${numero & 255}, ${opacidade})`;
}

/** Tons de seleção coerentes com a cor de marca, por esquema de cor. */
export function selectionToken(cor: string, escuro: boolean): ThemeConfig['token'] {
  if (!escuro) {
    return {
      colorPrimaryBg: tomClaro(cor, 0.12),
      colorPrimaryBgHover: tomClaro(cor, 0.2),
    };
  }

  const clara = paraModoEscuro(cor);
  return {
    colorPrimary: clara,
    colorLink: clara,
    colorPrimaryBg: veuDaCor(clara, 0.2),
    colorPrimaryBgHover: veuDaCor(clara, 0.28),
  };
}

/** Base compartilhada por app, admin e todos os módulos. */
export const baseToken: ThemeConfig['token'] = {
  colorPrimary: '#1f6f6b',
  /**
   * O Antd v6 tira o link do `colorInfo`, não do `colorPrimary`: sem isto, os
   * links do produto inteiro ficam no azul padrão, e a cor de cada módulo
   * deixa de aparecer justamente na navegação.
   */
  colorLink: '#1f6f6b',
  borderRadius: 8,
  /**
   * Cartão e modal ficam mais macios que os controles.
   *
   * O Antd deriva o raio grande do pequeno, e os dois saíam a 8: cartão de
   * produto com o mesmo canto de um campo de texto lê como formulário, não como
   * vitrine. Separar os dois é o gesto de desenho mais barato que existe.
   */
  borderRadiusLG: 16,
  fontSize: 15,
  fontFamily: fontFamilySans,
  wireframe: false,
};

/**
 * A tinta do texto, puxada para a cor da marca.
 *
 * O Antd deriva toda a escala de cinzas de um `colorTextBase` preto. Preto puro
 * sobre branco puro é o cinza de painel administrativo; marcas que passam
 * sensação de produto tingem o preto com o próprio matiz - a Voa usa um quase
 * preto ameixa com marca roxa, e a mesma ideia com o nosso verde dá um quase
 * preto levemente frio, que some como cor e aparece como acabamento.
 *
 * **Só no claro.** No escuro o texto nasce do branco, e o algoritmo escuro do
 * Antd já resolve o contraste; mexer ali trocaria acabamento por risco de
 * legibilidade, que é péssimo negócio.
 */
export function tintaBase(escuro: boolean): ThemeConfig['token'] {
  return escuro ? {} : { colorTextBase: '#141c1b' };
}

const moduleAccent: Record<ModuleKey, string> = {
  enac: '#1f6f6b',
  objective: '#2f5aa8',
  essay: '#8a4a2b',
};

export function moduleToken(module: ModuleKey, escuro = false): ThemeConfig['token'] {
  return acentoToken(moduleAccent[module], escuro);
}

/**
 * Acento de uma área que **não** é fase de prova.
 *
 * A Legislação Comentada precisa da mesma identidade visual dos módulos sem ser
 * um deles (D-20): ela é assinável, mora dentro de Cartórios na navegação, e não
 * tem `ModuleKey`. Separar o token do enum evita a alternativa ruim, que seria
 * inflar `ModuleKey` com algo que não é fase.
 */
export function acentoToken(acento: string, escuro = false): ThemeConfig['token'] {
  return {
    ...baseToken,
    colorPrimary: acento,
    colorLink: acento,
    ...selectionToken(acento, escuro),
  };
}

/** Ameixa: distinta do verde do ENAC, do azul da 1ª fase e do marrom da 2ª. */
export const libraryAccent = '#6b4a8a';

export const brandColor = '#1f6f6b';

/**
 * As cores do degradê da oferta, na ordem em que ele as atravessa.
 *
 * São **as três cores dos produtos**: o verde da marca (Cartórios), o azul da
 * 1ª fase e a ameixa da Legislação Comentada. Não é enfeite escolhido a esmo -
 * o botão diz "desbloquear tudo", e o degradê é literalmente tudo o que se
 * desbloqueia, na ordem em que a barra lateral os lista. Nenhuma cor nova entra
 * no produto por causa dele.
 *
 * **As mesmas três nos dois temas**, e não a versão clareada do
 * `paraModoEscuro` que o resto do tema usa. Medido no navegador em 06/09/2026:
 * clareado para o modo escuro, o verde vira `#33b7b1` e o texto branco em cima
 * dele cai para 2,1:1 - ilegível. No tom original os três seguram branco acima
 * de 4,5:1, e sobre a barra escura o bloco continua sendo a única coisa
 * colorida da tela: ele não precisa de brilho para ser visto.
 */
export const coresDaOferta: readonly [string, string, string] = [
  brandColor,
  moduleAccent.objective,
  libraryAccent,
];
